import { useEffect, useState } from "react";
import {
  useDeleteUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateUserStatusMutation,
} from "api/users";
import { useParams } from "react-router-dom";
import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  GetProp,
  Image,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import {
  UserOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  MailOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  LockOutlined,
  GlobalOutlined,
  AimOutlined,
  TagOutlined,
  HistoryOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  FileTextOutlined,
  DeleteOutlined,
  KeyOutlined,
  GoogleOutlined,
  AppleOutlined,
  FacebookOutlined,
  CameraOutlined,
  FileImageOutlined,
  AppstoreOutlined,
  ToolOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Tag as CustomTag, UploadGallery } from "components";
import {
  useGetCategoriesQuery,
  useGetCitiesQuery,
  useGetCountiesQuery,
  useGetCountriesQuery,
  useGetServicesQuery,
  UserStatuses,
  UserTypes,
} from "api";
import { SmartSelect } from "components/SmartSelect";
import { dateTimeFormat, displayUserName, format, formErrorHandler, getBase64, normFile } from "libs";
import { useAppSelector } from "store/hooks";
import { SetPasswordModal } from "./SetPasswordModal";
import { Gallery } from "../../../components/Gallery";

const { Title, Text, Paragraph } = Typography;

export type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

// Status configuration
interface StatusConfigItem {
  color: string;
  icon: React.ReactNode;
  text: string;
  bgColor: string;
}

const getStatusConfig = (status: UserStatuses): StatusConfigItem => {
  switch (status) {
    case UserStatuses.PENDING:
      return {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "În așteptare",
        bgColor: "#fff7e6",
      };
    case UserStatuses.ACTIVE:
      return {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Activ",
        bgColor: "#f6ffed",
      };
    case UserStatuses.BLOCKED:
      return {
        color: "red",
        icon: <LockOutlined />,
        text: "Blocat",
        bgColor: "#fff2f0",
      };
    default:
      return {
        color: "default",
        icon: <InfoCircleOutlined />,
        text: "Necunoscut",
        bgColor: "#fafafa",
      };
  }
};

const getUserTypeLabel = (type: UserTypes): string => {
  switch (type) {
    case UserTypes.PHYSICAL:
      return "Persoană fizică";
    case UserTypes.JURIDICAL:
      return "Persoană juridică";
    default:
      return "Necunoscut";
  }
};

// User types options for select
const userTypesOptions = [
  { value: UserTypes.PHYSICAL, label: "Persoană fizică" },
  { value: UserTypes.JURIDICAL, label: "Persoană juridică" },
];

export const UserInfo = () => {
  const params = useParams();
  const [form] = Form.useForm();
  const formValues = form.getFieldsValue(true, ({ touched }) => touched);
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [showResetPassModal, setShowResetPassModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdditionalAgreeModal, setShowAdditionalAgreeModal] = useState<UserStatuses | null>(null);
  const [reason, setReason] = useState<string>("");
  const [userPhoto, setUserPhoto] = useState<string>();
  const [profilePhotoIsHovered, setProfilePictureHover] = useState<boolean>(false);
  const [companyPhotoIsHovered, setCompanyPictureHover] = useState<boolean>(false);
  const [companyLogo, setCompanyLogo] = useState<string>();
  const [companyPhotos, setCompanyPhotos] = useState<string[]>([]);

  const { isAll, isUsersEdit, isUsersDelete } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions
  );

  const { data, isFetching, refetch } = useGetUserQuery({
    id: Number(params.userId),
  });

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery({});

  const { data: servicesData, isLoading: isServicesLoading } =
    useGetServicesQuery({});

  const [
    updateUserMutation,
    { isLoading, isSuccess: isSuccessUserUpdate, error: userUpdateError },
  ] = useUpdateUserMutation();

  const [
    updateUserStatusMutation,
    {
      isLoading: isUpdateUserStatusLoading,
      isSuccess: isUserUpdateStatusSuccess,
      error: updateStatusError,
    },
  ] = useUpdateUserStatusMutation();

  const [
    deleteUserMutation,
    { isLoading: isDeleteUserLoading, isSuccess, isError },
  ] = useDeleteUserMutation();

  const {
    data: dataCounties,
    isLoading: isCountiesLoading,
    refetch: refetchCounties,
  } = useGetCountiesQuery(
    {
      "filters[country_id][$eq]": formValues?.location?.country
        ? Number(formValues.location.country)
        : null,
    },
    { skip: !formValues?.location?.country || !isEditMode }
  );

  const {
    data: dataCities,
    isLoading: isCitiesLoading,
    refetch: refetchCities,
  } = useGetCitiesQuery(
    {
      "filters[county][$eq]": formValues?.location?.county
        ? Number(formValues.location.county)
        : null,
    },
    { skip: !formValues?.location?.county && !isEditMode }
  );

  const userTypeValue = Form.useWatch("type", form);
  const selectedCategories = Form.useWatch("categories", form);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        categories: data.categories?.map((i) => i.id),
        location: {
          country: data.location?.country?.id,
          county: data.location?.county?.id,
          city: data.location?.city?.id,
          address_details: data.location?.addressDetails,
        },
        profilePicture: [data.profilePicture],
        company: {
          ...data.company,
          logo: [data.company?.logo],
        },
      });
      setUserPhoto(data.profilePicture);
      setCompanyLogo(data.company?.logo);
    }
  }, [data, form]);

  useEffect(() => {
    if (isSuccess) {
      notification.success({
        message: "Succes!",
        description: "Utilizatorul a fost șters cu succes!",
      });
      setShowDeleteModal(false);
      setReason("");
    }
  }, [isSuccess, notification]);

  useEffect(() => {
    if (isSuccessUserUpdate) {
      setShowResetPassModal(false);
      setIsEditMode(false);
      refetch();
      notification.success({
        message: "Succes!",
        description: "Utilizatorul a fost modificat cu succes!",
      });
    }
  }, [isSuccessUserUpdate, notification, refetch]);

  useEffect(() => {
    if (userUpdateError) {
      form.setFields(
        formErrorHandler((userUpdateError as { data: unknown }).data)
      );
    }
  }, [userUpdateError, form]);

  useEffect(() => {
    if (isError) {
      notification.error({
        message: "Eroare",
        description: "Utilizatorul nu poate fi șters!",
      });
      setShowDeleteModal(false);
      setReason("");
    }
  }, [isError, notification]);

  useEffect(() => {
    if (isUserUpdateStatusSuccess) {
      notification.success({
        message: "Succes!",
        description: "Statusul utilizatorului a fost modificat cu succes!",
      });
      setShowAdditionalAgreeModal(null);
      setReason("");
    }
  }, [isUserUpdateStatusSuccess, notification]);

  useEffect(() => {
    if (updateStatusError) {
      notification.error({
        message: "Eroare",
        description: "Statusul utilizatorului nu poate fi modificat!",
      });
      setShowAdditionalAgreeModal(null);
    }
  }, [updateStatusError, notification]);

  const validateAndSubmit = () => {
    form
      .validateFields()
      .then(() => onFormSubmit())
      .catch((e) => console.log(e, "validation error"));
  };

  const onFormSubmit = async () => {
    const foundCity = dataCities?.find((i) => i.id === formValues.location.city);
    updateUserMutation({
      id: data?.id,
      ...formValues,
      company: {
        ...formValues.company,
        mediaFiles: [
          ...(data?.company?.mediaFiles?.map((i) => i.id) || []),
          ...companyPhotos,
        ],
        logo: companyLogo,
      },
      profilePicture: userPhoto,
      location: {
        ...formValues.location,
        city: foundCity?.name,
        latitude: foundCity?.latitude || 0,
        longitude: foundCity?.longitude || 0,
      },
    });
  };

  const onValuesChange = (changed: Record<string, unknown>) => {
    if (changed?.email) {
      form.setFieldValue("email", changed.email);
    }
    if (changed?.firstName) {
      form.setFieldValue("firstName", changed.firstName);
    }
    if (changed?.lastName) {
      form.setFieldValue("lastName", changed.lastName);
    }
    if (changed?.type) {
      form.setFieldValue("type", changed.type);
    }
    if (changed?.legalAgreements) {
      form.setFieldValue("legalAgreements", {
        ...formValues.legalAgreements,
        ...changed.legalAgreements,
      });
    }
    if (changed?.location) {
      const locationChange = changed.location as Record<string, unknown>;
      if (locationChange?.country) {
        form.setFieldValue("location", {
          ...formValues.location,
          ...locationChange,
          county: null,
          city: null,
        });
        refetchCounties();
      } else if (locationChange?.county) {
        form.setFieldValue("location", {
          ...formValues.location,
          ...locationChange,
          city: null,
        });
        refetchCities();
      } else if (locationChange?.city) {
        form.setFieldValue("location", {
          ...formValues.location,
          ...locationChange,
        });
      } else {
        form.setFieldValue("location", {
          ...formValues.location,
          ...locationChange,
        });
      }
    }
    if (changed?.notifications) {
      form.setFieldValue("notifications", {
        ...formValues.notifications,
        ...changed.notifications,
      });
    }
    if (changed?.categories) {
      form.setFieldValue("categories", changed.categories);
    }
    if (changed?.company) {
      form.setFieldValue("company", {
        ...formValues.company,
        ...changed.company,
      });
    }
  };

  const getStatusTag = (status: UserStatuses) => {
    const config = getStatusConfig(status);
    return (
      <Tag
        color={config.color}
        icon={config.icon}
        style={{ padding: "4px 12px", fontSize: 13 }}
      >
        {config.text}
      </Tag>
    );
  };

  const handleStatusChange = () => {
    if (showAdditionalAgreeModal !== null && data?.id) {
      updateUserStatusMutation({
        id: data.id,
        status: showAdditionalAgreeModal,
        reason: reason || undefined,
      });
    }
  };

  const handleCloseStatusModal = () => {
    setShowAdditionalAgreeModal(null);
    setReason("");
  };

  const handleDeleteUser = () => {
    if (data?.id) {
      deleteUserMutation({
        id: data.id,
        reason,
      });
    }
  };

  const uploadButton = (
    <div
      style={{
        width: 100,
        height: 100,
        borderRadius: "50%",
        border: "2px dashed #d9d9d9",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s",
        background: "#fafafa",
      }}
    >
      <CameraOutlined style={{ fontSize: 24, color: "#999" }} />
      <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
        Upload
      </Text>
    </div>
  );

  const uploadButtonSquare = (
    <div
      style={{
        width: 100,
        height: 100,
        borderRadius: 8,
        border: "2px dashed #d9d9d9",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s",
        background: "#fafafa",
      }}
    >
      <CameraOutlined style={{ fontSize: 24, color: "#999" }} />
      <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
        Upload
      </Text>
    </div>
  );

  // Loading skeleton
  if (isFetching) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
        </Card>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          padding: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Empty
          description="Utilizatorul nu a fost găsit"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "16px", maxWidth: 1600, margin: "0 auto" }}>
      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        onValuesChange={onValuesChange}
      >
        {/* Header Section */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          styles={{ body: { padding: "20px 24px" } }}
        >
          <Row gutter={[24, 16]} align="middle" justify="space-between">
            <Col xs={24} md={14}>
              <Space align="start" size={16}>
                <Avatar
                  size={72}
                  src={data.profilePicture}
                  icon={<UserOutlined />}
                  style={{ border: "3px solid #f0f0f0" }}
                />
                <Space direction="vertical" size={4}>
                  <Space wrap align="center">
                    <Title level={4} style={{ margin: 0 }}>
                      {displayUserName(data)}
                    </Title>
                    {getStatusTag(data.status)}
                    {data.isVerified && (
                      <Tag color="blue" icon={<SafetyCertificateOutlined />}>
                        Verificat
                      </Tag>
                    )}
                    {data.deletedAt && (
                      <Tag color="red" icon={<DeleteOutlined />}>
                        Șters
                      </Tag>
                    )}
                  </Space>
                  <Space split={<Divider type="vertical" />} wrap>
                    <Text type="secondary">
                      <MailOutlined style={{ marginRight: 6 }} />
                      {data.email}
                    </Text>
                    <Text type="secondary">
                      <TagOutlined style={{ marginRight: 6 }} />
                      {getUserTypeLabel(data.type)}
                    </Text>
                    <Text type="secondary">ID: #{data.id}</Text>
                  </Space>
                  {data.isSocialMediaAccount && (
                    <Space style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Conectat prin:
                      </Text>
                      <Space size={4}>
                        <Tooltip title="Google">
                          <GoogleOutlined style={{ color: "#DB4437" }} />
                        </Tooltip>
                        <Tooltip title="Apple">
                          <AppleOutlined style={{ color: "#000" }} />
                        </Tooltip>
                        <Tooltip title="Facebook">
                          <FacebookOutlined style={{ color: "#4267B2" }} />
                        </Tooltip>
                      </Space>
                    </Space>
                  )}
                </Space>
              </Space>
            </Col>

            <Col xs={24} md={10}>
              <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
                {(isAll || isUsersEdit) && (
                  <>
                    {isEditMode ? (
                      <>
                        <Button onClick={() => setIsEditMode(false)}>
                          Anulează
                        </Button>
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={validateAndSubmit}
                          loading={isLoading}
                        >
                          Salvează
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        onClick={() => setIsEditMode(true)}
                      >
                        Editează
                      </Button>
                    )}

                    {data.status === UserStatuses.PENDING && (
                      <>
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={() =>
                            setShowAdditionalAgreeModal(UserStatuses.ACTIVE)
                          }
                          style={{
                            background: "#52c41a",
                            borderColor: "#52c41a",
                          }}
                        >
                          Aprobă
                        </Button>
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() =>
                            setShowAdditionalAgreeModal(UserStatuses.BLOCKED)
                          }
                        >
                          Respinge
                        </Button>
                      </>
                    )}

                    <Button
                      icon={<KeyOutlined />}
                      onClick={() => setShowResetPassModal(true)}
                    >
                      Resetează parola
                    </Button>
                  </>
                )}

                {(isAll || isUsersDelete) && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Șterge
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Deleted User Alert */}
        {data.deletedAt && (
          <Alert
            message="Utilizator șters"
            description={`Acest utilizator a fost șters la data: ${format(
              data.deletedAt,
              dateTimeFormat
            )}`}
            type="error"
            showIcon
            icon={<DeleteOutlined />}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        <Row gutter={[24, 24]}>
          {/* Left Column - Main Content */}
          <Col xs={24} xl={16}>
            {/* User Details Card */}
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>Informații utilizator</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              {/* Profile Picture */}
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  POZĂ DE PROFIL
                </Text>
                <Form.Item
                  name="profilePicture"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  style={{ marginBottom: 0 }}
                >
                  {isEditMode ? (
                    <div
                      onMouseEnter={() => setProfilePictureHover(true)}
                      onMouseLeave={() => setProfilePictureHover(false)}
                      style={{ display: "inline-block" }}
                    >
                      <Upload
                        customRequest={(uploadData) =>
                          getBase64(uploadData.file as FileType, (url) => {
                            setUserPhoto(url);
                          })
                        }
                        showUploadList={false}
                      >
                        {profilePhotoIsHovered ? (
                          uploadButton
                        ) : (
                          <Avatar
                            src={userPhoto}
                            size={100}
                            icon={<UserOutlined />}
                            style={{ cursor: "pointer", border: "3px solid #f0f0f0" }}
                          />
                        )}
                      </Upload>
                    </div>
                  ) : (
                    <Avatar
                      src={data.profilePicture}
                      size={100}
                      icon={<UserOutlined />}
                      style={{ border: "3px solid #f0f0f0" }}
                    />
                  )}
                </Form.Item>
              </div>

              <Row gutter={[24, 24]}>
                {/* First Name */}
                <Col xs={24} sm={12}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    PRENUME
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name="firstName"
                      rules={[
                        { required: true, message: "Prenumele este obligatoriu" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Introdu prenumele"
                        size="large"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>{data.firstName}</Text>
                  )}
                </Col>

                {/* Last Name */}
                <Col xs={24} sm={12}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    NUME
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name="lastName"
                      rules={[
                        { required: true, message: "Numele este obligatoriu" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Introdu numele"
                        size="large"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>{data.lastName}</Text>
                  )}
                </Col>

                {/* Email */}
                <Col xs={24} sm={12}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <MailOutlined style={{ marginRight: 6 }} />
                    EMAIL
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="email" style={{ marginBottom: 0 }}>
                      <Input
                        placeholder="Introdu email-ul"
                        size="large"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : (
                    <Text
                      style={{ fontSize: 15 }}
                      copyable={{ tooltips: ["Copiază", "Copiat!"] }}
                    >
                      {data.email}
                    </Text>
                  )}
                </Col>

                {/* Type */}
                <Col xs={24} sm={12}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    TIP UTILIZATOR
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="type" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Alege tipul"
                        size="large"
                        options={userTypesOptions}
                      />
                    </Form.Item>
                  ) : (
                    <Tag
                      color={
                        data.type === UserTypes.JURIDICAL ? "purple" : "blue"
                      }
                      icon={
                        data.type === UserTypes.JURIDICAL ? (
                          <ShopOutlined />
                        ) : (
                          <UserOutlined />
                        )
                      }
                      style={{ padding: "6px 16px", fontSize: 14 }}
                    >
                      {getUserTypeLabel(data.type)}
                    </Tag>
                  )}
                </Col>

                {/* Description / Bio */}
                <Col xs={24}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    DESCRIERE / BIO
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="description" style={{ marginBottom: 0 }}>
                      <Input.TextArea
                        rows={4}
                        placeholder="Introdu descrierea"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : (
                    <Paragraph
                      style={{
                        margin: 0,
                        fontSize: 14,
                        lineHeight: 1.8,
                        color: "#333",
                      }}
                    >
                      {data.description || "Fără descriere"}
                    </Paragraph>
                  )}
                </Col>

                {/* Website / Link in bio */}
                <Col xs={24}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    WEBSITE
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name="website"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          max: 100,
                          message: "Maxim 100 de caractere",
                        },
                        {
                          pattern:
                            /^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s]*)?$/i,
                          message: "Link invalid (ex: exemplu.com)",
                        },
                      ]}
                    >
                      <Input
                        placeholder="exemplu.com"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : data.website ? (
                    <a
                      href={data.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 14, wordBreak: "break-all" }}
                    >
                      {data.website}
                    </a>
                  ) : (
                    <Paragraph style={{ margin: 0, color: "#999" }}>
                      Fără website
                    </Paragraph>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Categories Card */}
            <Card
              title={
                <Space>
                  <AppstoreOutlined />
                  <span>Categorii</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              {isEditMode ? (
                <Form.Item name="categories" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    loading={isCategoriesLoading}
                    placeholder="Selectează categoriile"
                    size="large"
                    style={{ width: "100%" }}
                  >
                    {categoriesData?.map((i) => (
                      <Select.Option value={i.id} key={i.id}>
                        {i.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : data.categories && data.categories.length > 0 ? (
                <Space wrap>
                  {data.categories.map((i) => (
                    <Tag
                      key={i.id}
                      color="blue"
                      style={{ padding: "4px 12px", fontSize: 13 }}
                    >
                      {i.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Fără categorii"
                />
              )}
            </Card>

            {/* Company Card */}
            {(data.company?.id || userTypeValue === UserTypes.JURIDICAL) && (
              <Card
                title={
                  <Space>
                    <ShopOutlined />
                    <span>Companie</span>
                  </Space>
                }
                style={{
                  marginBottom: 24,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
              >
                {/* Company Logo */}
                <div style={{ marginBottom: 24 }}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 12,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    LOGO COMPANIE
                  </Text>
                  <Form.Item
                    name={["company", "logo"]}
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    style={{ marginBottom: 0 }}
                  >
                    {isEditMode ? (
                      <div
                        onMouseEnter={() => setCompanyPictureHover(true)}
                        onMouseLeave={() => setCompanyPictureHover(false)}
                        style={{ display: "inline-block" }}
                      >
                        <Upload
                          customRequest={(uploadData) =>
                            getBase64(uploadData.file as FileType, (url) => {
                              setCompanyLogo(url);
                            })
                          }
                          showUploadList={false}
                        >
                          {companyPhotoIsHovered ? (
                            uploadButtonSquare
                          ) : (
                            <Avatar
                              src={companyLogo}
                              size={100}
                              shape="square"
                              icon={<ShopOutlined />}
                              style={{
                                cursor: "pointer",
                                borderRadius: 8,
                                border: "1px solid #f0f0f0",
                              }}
                            />
                          )}
                        </Upload>
                      </div>
                    ) : (
                      <Avatar
                        src={data.company?.logo}
                        size={100}
                        shape="square"
                        icon={<ShopOutlined />}
                        style={{ borderRadius: 8, border: "1px solid #f0f0f0" }}
                      />
                    )}
                  </Form.Item>
                </div>

                <Row gutter={[24, 24]}>
                  {/* Company Name */}
                  <Col xs={24} sm={12}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      DENUMIRE
                    </Text>
                    {isEditMode ? (
                      <Form.Item
                        name={["company", "name"]}
                        rules={[
                          {
                            required: true,
                            message: "Denumirea este obligatorie",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Introdu denumirea"
                          size="large"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    ) : (
                      <Text style={{ fontSize: 15 }}>
                        {data.company?.name || "-"}
                      </Text>
                    )}
                  </Col>

                  {/* CUI */}
                  <Col xs={24} sm={12}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      CUI
                    </Text>
                    {isEditMode ? (
                      <Form.Item
                        name={["company", "CUI"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Introdu CUI"
                          size="large"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    ) : (
                      <Text style={{ fontSize: 15 }} code>
                        {data.company?.CUI || "-"}
                      </Text>
                    )}
                  </Col>

                  {/* Registration Number */}
                  <Col xs={24} sm={12}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      NR. ÎNREGISTRARE
                    </Text>
                    {isEditMode ? (
                      <Form.Item
                        name={["company", "registration_number"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Introdu nr. înregistrare"
                          size="large"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    ) : (
                      <Text style={{ fontSize: 15 }} code>
                        {data.company?.registration_number || "-"}
                      </Text>
                    )}
                  </Col>

                  {/* Company Description */}
                  <Col xs={24}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      DESCRIERE COMPANIE
                    </Text>
                    {isEditMode ? (
                      <Form.Item
                        name={["company", "description"]}
                        rules={[
                          {
                            required: true,
                            message: "Descrierea este obligatorie",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder="Introdu descrierea companiei"
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    ) : (
                      <Paragraph
                        style={{
                          margin: 0,
                          fontSize: 14,
                          lineHeight: 1.8,
                          color: "#333",
                        }}
                      >
                        {data.company?.description || "Fără descriere"}
                      </Paragraph>
                    )}
                  </Col>

                  {/* Company Media Files */}
                  <Col xs={24}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 12,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      GALERIE POZE ({data.company?.mediaFiles?.length || 0})
                    </Text>
                    <Form.Item
                      name={["company", "mediaFiles"]}
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                      style={{ marginBottom: 0 }}
                    >
                      {isEditMode ? (
                        <UploadGallery
                          customRequest={() => null}
                          onImagesChange={setCompanyPhotos}
                        />
                      ) : data.company?.mediaFiles &&
                        data.company.mediaFiles.length > 0 ? (
                        <Gallery mediaFiles={data.company.mediaFiles} />
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Fără poze"
                        />
                      )}
                    </Form.Item>
                  </Col>

                  {/* Services */}
                  <Col xs={24}>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 13,
                      }}
                    >
                      <ToolOutlined style={{ marginRight: 6 }} />
                      SERVICII
                    </Text>
                    {isEditMode ? (
                      <Form.Item
                        name={["company", "services"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          mode="multiple"
                          loading={isServicesLoading}
                          placeholder="Selectează serviciile"
                          size="large"
                          style={{ width: "100%" }}
                        >
                          {servicesData
                            ?.filter((i) =>
                              selectedCategories?.includes(i.category?.id)
                            )
                            .map((i) => (
                              <Select.Option value={i.id} key={i.id}>
                                {i.name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>
                    ) : data.services && data.services.length > 0 ? (
                      <Space wrap>
                        {data.services.map((i) => (
                          <Tag
                            key={i.id}
                            color="cyan"
                            style={{ padding: "4px 12px", fontSize: 13 }}
                          >
                            {i.name}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">Fără servicii</Text>
                    )}
                  </Col>
                </Row>
              </Card>
            )}

            {/* Location Card */}
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  <span>Locație</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <GlobalOutlined style={{ marginRight: 6 }} />
                    ȚARĂ
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["location", "country"]}
                      rules={[
                        { required: true, message: "Țara este obligatorie" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <SmartSelect
                        query={useGetCountriesQuery}
                        placeholder="Alege țara"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.location?.country?.name || "-"}
                    </Text>
                  )}
                </Col>

                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <EnvironmentOutlined style={{ marginRight: 6 }} />
                    JUDEȚ
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["location", "county"]}
                      rules={[
                        { required: true, message: "Județul este obligatoriu" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        loading={isCountiesLoading}
                        placeholder="Alege județul"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                        disabled={!formValues?.location?.country}
                      >
                        {dataCounties?.map((i) => (
                          <Select.Option key={`county-${i.id}`} value={i.id}>
                            {i.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.location?.county?.name || "-"}
                    </Text>
                  )}
                </Col>

                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <AimOutlined style={{ marginRight: 6 }} />
                    ORAȘ
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["location", "city"]}
                      rules={[
                        { required: true, message: "Orașul este obligatoriu" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        loading={isCitiesLoading}
                        placeholder="Alege orașul"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                        disabled={!formValues?.location?.county}
                      >
                        {dataCities?.map((i) => (
                          <Select.Option key={`city-${i.id}`} value={i.id}>
                            {i.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.location?.city?.name || "-"}
                    </Text>
                  )}
                </Col>

                <Col xs={24}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    ADRESĂ DETALIATĂ
                  </Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["location", "address_details"]}
                      rules={[
                        { required: true, message: "Adresa este obligatorie" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Introdu adresa"
                        size="large"
                        style={{ borderRadius: 8 }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.location?.addressDetails || "-"}
                    </Text>
                  )}
                </Col>

                {data.location?.latitude && data.location?.longitude && (
                  <Col xs={24}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      <AimOutlined style={{ marginRight: 6 }} />
                      Coordonate: {data.location.latitude},{" "}
                      {data.location.longitude}
                    </Text>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>

          {/* Right Column - Sidebar */}
          <Col xs={24} xl={8}>
            {/* Status & Activity Card */}
            <Card
              title={
                <Space>
                  <InfoCircleOutlined />
                  <span>Status & Activitate</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary">Status</Text>
                  {getStatusTag(data.status)}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary">Verificat</Text>
                  {data.isVerified ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Da
                    </Tag>
                  ) : (
                    <Tag color="default" icon={<CloseCircleOutlined />}>
                      Nu
                    </Tag>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary">Cont social</Text>
                  {data.isSocialMediaAccount ? (
                    <Tag color="blue">Da</Tag>
                  ) : (
                    <Tag color="default">Nu</Tag>
                  )}
                </div>

                <Divider style={{ margin: "8px 0" }} />

                <div>
                  <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                    <HistoryOutlined style={{ marginRight: 6 }} />
                    Ultima activitate
                  </Text>
                  <Text style={{ fontSize: 14 }}>
                    {data.lastActivity
                      ? format(data.lastActivity, dateTimeFormat)
                      : "Necunoscută"}
                  </Text>
                </div>
              </Space>
            </Card>

            {/* Legal Agreements Card */}
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>Acorduri legale</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Termeni și condiții</Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["legalAgreements", "termsAndConditions"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  ) : data.legalAgreements?.termsAndConditions ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Acceptat
                    </Tag>
                  ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Neacceptat
                    </Tag>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Politica de confidențialitate</Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["legalAgreements", "privacyPolicy"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  ) : data.legalAgreements?.privacyPolicy ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Acceptat
                    </Tag>
                  ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Neacceptat
                    </Tag>
                  )}
                </div>
              </Space>
            </Card>

            {/* Notifications Card */}
            <Card
              title={
                <Space>
                  <BellOutlined />
                  <span>Notificări</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Comentarii noi</Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["notifications", "notifiedOnNewComments"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  ) : data.notifications?.notifiedOnNewComments ? (
                    <Tag color="green">Activ</Tag>
                  ) : (
                    <Tag color="default">Inactiv</Tag>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Postări noi</Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["notifications", "notifiedOnNewPosts"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  ) : data.notifications?.notifiedOnNewPosts ? (
                    <Tag color="green">Activ</Tag>
                  ) : (
                    <Tag color="default">Inactiv</Tag>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Mesaje noi</Text>
                  {isEditMode ? (
                    <Form.Item
                      name={["notifications", "notifiedOnNewMessages"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  ) : data.notifications?.notifiedOnNewMessages ? (
                    <Tag color="green">Activ</Tag>
                  ) : (
                    <Tag color="default">Inactiv</Tag>
                  )}
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Reset Password Modal */}
      {showResetPassModal && (
        <SetPasswordModal
          onCancel={() => setShowResetPassModal(false)}
          mutation={updateUserMutation}
          isLoading={isLoading}
        />
      )}

      {/* Status Change Modal */}
      <Modal
        open={showAdditionalAgreeModal !== null}
        title={
          <Space>
            {showAdditionalAgreeModal === UserStatuses.ACTIVE ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
            )}
            <span>Confirmare modificare status</span>
          </Space>
        }
        okText={
          showAdditionalAgreeModal === UserStatuses.ACTIVE
            ? "Aprobă"
            : "Respinge"
        }
        cancelText="Anulează"
        onCancel={handleCloseStatusModal}
        onOk={handleStatusChange}
        okButtonProps={{
          loading: isUpdateUserStatusLoading,
          disabled:
            showAdditionalAgreeModal === UserStatuses.BLOCKED &&
            !reason.trim(),
          danger: showAdditionalAgreeModal === UserStatuses.BLOCKED,
          style:
            showAdditionalAgreeModal === UserStatuses.ACTIVE
              ? { background: "#52c41a", borderColor: "#52c41a" }
              : {},
        }}
        centered
        width={480}
      >
        <div style={{ padding: "20px 0" }}>
          <Alert
            message={
              showAdditionalAgreeModal === UserStatuses.ACTIVE
                ? "Utilizatorul va fi activat și va putea accesa platforma."
                : "Utilizatorul va fi blocat și nu va mai putea accesa platforma."
            }
            type={
              showAdditionalAgreeModal === UserStatuses.ACTIVE
                ? "success"
                : "warning"
            }
            showIcon
            style={{ marginBottom: 20 }}
          />

          {showAdditionalAgreeModal === UserStatuses.BLOCKED && (
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Motivul respingerii *
              </Text>
              <Input.TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Introdu motivul respingerii..."
                rows={3}
                style={{ borderRadius: 8 }}
              />
              <Text
                type="secondary"
                style={{ fontSize: 12, marginTop: 8, display: "block" }}
              >
                Acest mesaj va fi vizibil pentru utilizator.
              </Text>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={showDeleteModal}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            <span>Ștergere utilizator</span>
          </Space>
        }
        okText="Șterge"
        cancelText="Anulează"
        onCancel={() => {
          setShowDeleteModal(false);
          setReason("");
        }}
        onOk={handleDeleteUser}
        okButtonProps={{
          loading: isDeleteUserLoading,
          disabled: !reason.trim(),
          danger: true,
        }}
        centered
        width={480}
      >
        <div style={{ padding: "20px 0" }}>
          <Alert
            message="Atenție!"
            description="Această acțiune este ireversibilă. Utilizatorul și toate datele asociate vor fi șterse permanent."
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />

          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Motivul ștergerii *
            </Text>
            <Input.TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Introdu motivul ștergerii..."
              rows={3}
              style={{ borderRadius: 8 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};